import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  Wrench,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  Bell,
  ChevronRight,
  MapPin,
  CircleDot,
} from 'lucide-react';
import TenantLayout from '../components/TenantLayout';
import { getApiUrl } from '../config/api';

// Status badge configuration
const statusConfig = {
  open: { label: 'Open', bg: 'bg-[#FEF3C7]', text: 'text-[#92400E]', dot: 'bg-[#F59E0B]' },
  'in-progress': { label: 'In Progress', bg: 'bg-[#DBEAFE]', text: 'text-[#1E40AF]', dot: 'bg-[#3498DB]' },
  scheduled: { label: 'Scheduled', bg: 'bg-[#F0FDF4]', text: 'text-[#166534]', dot: 'bg-[#16A34A]' },
};

// Timeline event icon configuration
const timelineIconConfig = {
  'service-scheduled': { icon: Calendar, color: 'text-[#16A34A]', bg: 'bg-[#F0FDF4]' },
  'request-updated': { icon: AlertCircle, color: 'text-[#3498DB]', bg: 'bg-[#EFF6FF]' },
  message: { icon: MessageSquare, color: 'text-[#06B6D4]', bg: 'bg-[#ECFEFF]' },
  completed: { icon: CheckCircle, color: 'text-[#16A34A]', bg: 'bg-[#F0FDF4]' },
};

function TenantDashboard() {
  const navigate = useNavigate();
  const firstName = localStorage.getItem('first_name') || 'Tenant';

  // Service requests state
  const [serviceRequests, setServiceRequests] = useState([
    {
      id: 1,
      title: 'Leaking kitchen faucet',
      status: 'scheduled',
      submitted: 'Mar 5, 2026',
      description: 'Faucet drips continuously when turned off',
    },
    {
      id: 2,
      title: 'HVAC not heating properly',
      status: 'in-progress',
      submitted: 'Mar 3, 2026',
      description: 'Thermostat set to 72°F but unit only reaches 65°F',
    },
    {
      id: 3,
      title: 'Broken window latch — bedroom',
      status: 'open',
      submitted: 'Mar 10, 2026',
      description: 'Latch snapped, window won\'t lock securely',
    },
  ]);

  // Timeline events state
  const [timelineEvents, setTimelineEvents] = useState([
    {
      id: 1,
      type: 'service-scheduled',
      title: 'Plumber visit scheduled',
      description: 'For kitchen faucet repair — Mike\'s Plumbing',
      date: 'Mar 14, 2026',
      time: '10:00 AM',
    },
    {
      id: 2,
      type: 'request-updated',
      title: 'HVAC repair update',
      description: 'Technician ordered replacement part, ETA 2 days',
      date: 'Mar 10, 2026',
      time: '3:15 PM',
    },
    {
      id: 3,
      type: 'message',
      title: 'Message from landlord',
      description: 'Thanks for reporting the window latch — we\'ll get that fixed ASAP.',
      date: 'Mar 10, 2026',
      time: '1:30 PM',
    },
    {
      id: 4,
      type: 'completed',
      title: 'Garbage disposal replaced',
      description: 'Service request #104 completed successfully',
      date: 'Mar 1, 2026',
      time: '11:00 AM',
    },
    {
      id: 5,
      type: 'service-scheduled',
      title: 'Annual smoke detector check',
      description: 'Building-wide inspection by management',
      date: 'Mar 20, 2026',
      time: '9:00 AM',
    },
  ]);

  // Property info state
  const [property, setProperty] = useState({
    address: '123 Main Street, Apt 2B',
    city: 'Springfield, IL 62704',
  });

  // Computed stats
  const openCount = serviceRequests.filter(r => r.status === 'open').length;
  const inProgressCount = serviceRequests.filter(r => r.status === 'in-progress').length;
  const scheduledCount = serviceRequests.filter(r => r.status === 'scheduled').length;

  return (
    <TenantLayout
      pageTitle={`Hi, ${firstName}!`}
      pageSubtitle="Here's what's happening at your place"
    >
      <div className="max-w-5xl">
        {/* Property Card */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#F0FDF4] flex items-center justify-center shrink-0">
              <MapPin size={20} className="text-[#16A34A]" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-[#7F8C8D] mb-0.5">Your Property</p>
              <p className="font-medium text-[#2C3E50]">{property.address}</p>
              <p className="text-sm text-[#7F8C8D]">{property.city}</p>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#FEF3C7] flex items-center justify-center shrink-0">
              <CircleDot size={18} className="text-[#F59E0B]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#2C3E50]">{openCount}</p>
              <p className="text-xs text-[#7F8C8D]">Open</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#DBEAFE] flex items-center justify-center shrink-0">
              <Clock size={18} className="text-[#3498DB]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#2C3E50]">{inProgressCount}</p>
              <p className="text-xs text-[#7F8C8D]">In Progress</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#F0FDF4] flex items-center justify-center shrink-0">
              <Calendar size={18} className="text-[#16A34A]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#2C3E50]">{scheduledCount}</p>
              <p className="text-xs text-[#7F8C8D]">Scheduled</p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Active Service Requests */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-[#E2E8F0]">
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#2C3E50]">Active Service Requests</h3>
                <span className="text-xs text-[#7F8C8D]">{serviceRequests.length} total</span>
              </div>
              <div className="divide-y divide-[#E2E8F0]">
                {serviceRequests.map((req) => {
                  const sc = statusConfig[req.status];
                  return (
                    <div
                      key={req.id}
                      className="px-5 py-4 hover:bg-[#FAFAFA] transition-colors cursor-pointer group"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-[#2C3E50] truncate">{req.title}</p>
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full shrink-0 ${sc.bg} ${sc.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                              {sc.label}
                            </span>
                          </div>
                          <p className="text-sm text-[#7F8C8D] truncate">{req.description}</p>
                          <p className="text-xs text-[#7F8C8D] mt-1">Submitted {req.submitted}</p>
                        </div>
                        <ChevronRight size={16} className="text-[#7F8C8D] mt-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-3 border-t border-[#E2E8F0]">
                <button
                  onClick={() => navigate('/tenant/service')}
                  className="text-sm text-[#16A34A] hover:text-[#27AE60] font-medium transition-colors"
                >
                  + Submit New Request
                </button>
              </div>
            </div>
          </div>

          {/* Event Timeline */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-[#E2E8F0]">
              <div className="px-5 py-4 border-b border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#2C3E50]">Event Timeline</h3>
              </div>
              <div className="px-5 py-3">
                <div className="relative">
                  {/* Timeline vertical line */}
                  <div className="absolute left-[15px] top-4 bottom-4 w-px bg-[#E2E8F0]"></div>

                  <div className="space-y-5">
                    {timelineEvents.map((event) => {
                      const config = timelineIconConfig[event.type];
                      const Icon = config.icon;
                      const isFuture = new Date(event.date) > new Date();

                      return (
                        <div key={event.id} className="flex gap-3 relative">
                          <div className={`w-[30px] h-[30px] rounded-full ${config.bg} flex items-center justify-center shrink-0 z-10 border-2 border-white`}>
                            <Icon size={14} className={config.color} />
                          </div>
                          <div className="min-w-0 pt-0.5">
                            <p className={`text-sm font-medium ${isFuture ? 'text-[#2C3E50]' : 'text-[#7F8C8D]'}`}>
                              {event.title}
                            </p>
                            <p className="text-xs text-[#7F8C8D] mt-0.5 truncate">{event.description}</p>
                            <p className="text-xs text-[#7F8C8D] mt-1">
                              {isFuture && <span className="text-[#16A34A] font-medium">Upcoming · </span>}
                              {event.date} at {event.time}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TenantLayout>
  );
}

export default TenantDashboard;
